import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { peerEligibilityEndpoint } from "@/config/endpoints";

export interface PeerUser {
  id: number;
  name: string | null;
  avatar: string | null;
  role: string | null;
  roleId: number | null;
}

/**
 * Eligible same-role peers the current user may hand a lead/deal off to.
 * Authoritative server check: same role + branch + custom-field rules (the
 * same `filterAssignableUsers` stack the write path uses). The picker shows
 * ONLY these — you can't select a peer the transfer would reject.
 *
 * `enabled` should be the drawer's open state so we don't fetch until the
 * peer sheet is shown. `childId` set → deal-level transfer; null → lead-level.
 */
export default function usePeerEligibility(
  leadId: number | null | undefined,
  childId: number | null | undefined,
  enabled: boolean,
) {
  const query = useQuery({
    queryKey: ["peer-eligibility", leadId ?? null, childId ?? null],
    queryFn: async () => {
      const res = await axiosInstance.post(peerEligibilityEndpoint, {
        leadId,
        childId: childId ?? null,
      });
      return (res.data?.users ?? []) as PeerUser[];
    },
    enabled: Boolean(enabled && leadId),
    staleTime: 0,
  });

  return {
    peers: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    /** The server's refusal, e.g. "Your role can't reassign leads in the "Visa" stage. …" */
    errorMessage: ((query.error as any)?.response?.data?.message as string | undefined) ?? null,
    refetch: query.refetch,
  };
}
